import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, ApiError, type ChatMessage, type ChatQuota, type ChatReply } from './api';

/** A thread message plus a transient `pending` flag for the optimistic placeholder. */
export type ThreadMessage = ChatMessage & { pending?: boolean };

/** Assistant placeholder shown while a POST is in flight. */
export const THINKING_PLACEHOLDER = 'thinking…';

const chatHistoryKey = (vehicleId: string) => ['chat', vehicleId, 'history'] as const;
const chatQuotaKey = (vehicleId: string) => ['chat', vehicleId, 'quota'] as const;

export function useChatHistory(vehicleId: string) {
  return useQuery<ThreadMessage[], ApiError>({
    queryKey: chatHistoryKey(vehicleId),
    queryFn: () => api.getChatHistory(vehicleId),
    enabled: Boolean(vehicleId),
    retry: false,
  });
}

export function useChatQuota(vehicleId: string) {
  return useQuery<ChatQuota, ApiError>({
    queryKey: chatQuotaKey(vehicleId),
    queryFn: () => api.getChatQuota(vehicleId),
    enabled: Boolean(vehicleId),
    retry: false,
  });
}

interface SendContext {
  previous: ThreadMessage[];
  placeholderId: string;
}

function mkMessage(id: string, role: 'user' | 'assistant', content: string): ThreadMessage {
  return { id, role, content, codesCited: [], recallsCited: [], createdAt: new Date().toISOString() };
}

/**
 * Optimistically appends the user bubble + a "thinking…" placeholder, replaces
 * the placeholder with the real answer on success (and updates the quota meter
 * from `reply.quota`), and rolls the thread back on error.
 */
export function useSendChatMessage(vehicleId: string) {
  const qc = useQueryClient();
  return useMutation<ChatReply, ApiError, string, SendContext>({
    mutationFn: (message) => api.sendChatMessage(vehicleId, message),
    onMutate: async (message) => {
      await qc.cancelQueries({ queryKey: chatHistoryKey(vehicleId) });
      const previous = qc.getQueryData<ThreadMessage[]>(chatHistoryKey(vehicleId)) ?? [];
      const stamp = Date.now();
      const userMsg = mkMessage(`optimistic-user-${stamp}`, 'user', message);
      const placeholder: ThreadMessage = {
        ...mkMessage(`optimistic-assistant-${stamp}`, 'assistant', THINKING_PLACEHOLDER),
        pending: true,
      };
      qc.setQueryData<ThreadMessage[]>(chatHistoryKey(vehicleId), [...previous, userMsg, placeholder]);
      return { previous, placeholderId: placeholder.id };
    },
    onSuccess: (reply, _message, ctx) => {
      if (!ctx) return;
      qc.setQueryData<ThreadMessage[]>(chatHistoryKey(vehicleId), (cur) =>
        (cur ?? []).map((m) =>
          m.id === ctx.placeholderId
            ? { ...m, content: reply.reply, codesCited: reply.codesCited, recallsCited: reply.recallsCited, pending: false }
            : m,
        ),
      );
      qc.setQueryData<ChatQuota>(chatQuotaKey(vehicleId), reply.quota);
    },
    onError: (err, _message, ctx) => {
      if (ctx) qc.setQueryData<ThreadMessage[]>(chatHistoryKey(vehicleId), ctx.previous);
      // A 403 means the daily quota is spent — reflect an empty meter. Guard the
      // instanceof: network/parse failures arrive as non-ApiError errors.
      if (err instanceof ApiError && err.status === 403) {
        qc.setQueryData<ChatQuota>(chatQuotaKey(vehicleId), (cur) =>
          cur ? { ...cur, used: cur.limit, remaining: 0 } : cur,
        );
      }
    },
  });
}
